import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import {
  kMaterialTypes,
  kQuotationMaterialInventoryStatusInfoMap,
} from '@euler/app/flows/task/pages/quotation/functions/constants';
import { Center } from '@euler/components/Center';
import { Stepper } from '@euler/components/form/Stepper';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { QuotationMaterialItem } from '@euler/model/task-detail';
import { memo } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { PriceInfoView } from './PriceInfoView';

const formatQty = (qty: number) => `x ${qty}`;

export const ReadonlyMaterialItemView = memo(
  ({
    material,
    style,
  }: {
    material: QuotationMaterialItem;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();

    return (
      <StackLayout
        direction="row"
        style={style}
        gap={rpx(4)}
        alignItems="center"
      >
        <StackLayout flex={1} gap={rpx(4)}>
          <StackLayout
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            gap={rpx(5)}
          >
            <StackLayout
              direction="row"
              alignItems="center"
              gap={rpx(2)}
              flex={1}
            >
              <Typography.Text
                regular
                color="#444"
                size={rpx(16)}
                lineHeight={rpx(24)}
              >
                {material.name}
              </Typography.Text>
              {material.partNumber?.trim() ? (
                <Typography.Text
                  light
                  opacity={0.5}
                  size={rpx(13)}
                  lineHeight={rpx(24)}
                >{`(配件号: ${material.partNumber.trim()})`}</Typography.Text>
              ) : null}
            </StackLayout>
          </StackLayout>
          {material.brandName?.trim().length ||
          material.inventoryStatus != null ||
          material.materialType != null ? (
            <StackLayout direction="row" alignItems="center" gap={rpx(4)}>
              {material.materialType != null ? (
                <Tag
                  label={
                    kMaterialTypes[material.materialType]?.name ??
                    material.materialType
                  }
                />
              ) : null}
              {material.brandName?.trim().length ? (
                <Tag label={material.brandName} />
              ) : null}
              {material.inventoryStatus != null ? (
                <Tag
                  label={
                    kQuotationMaterialInventoryStatusInfoMap[
                      material.inventoryStatus
                    ]?.name ?? material.inventoryStatus
                  }
                />
              ) : null}
            </StackLayout>
          ) : null}
          <HStack>
            <HStack
              css={`
                flex: 1;
              `}
            >
              <Typography.Text color="#777" flexShrink={0} numberOfLines={1}>
                配件费:{' '}
              </Typography.Text>
              <PriceInfoView
                price={material.price}
                discountPrice={material.discountPrice}
                discountRemark={material.discountRemark}
              />
            </HStack>
            <Stepper
              editable={false}
              value={material.qty ?? 1}
              min={1}
              max={1000}
              format={formatQty}
              css={`
                margin-right: ${rpx(-2)}px;
              `}
            />
          </HStack>
          {material.remark ? (
            <StackLayout direction="row">
              <Typography.Text color="#777" flexShrink={0}>
                备注:{' '}
              </Typography.Text>
              <Typography.Text>{material.remark}</Typography.Text>
            </StackLayout>
          ) : null}
        </StackLayout>
      </StackLayout>
    );
  },
);

const Tag = memo(
  ({ label, style }: { label: string; style?: StyleProp<ViewStyle> }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Center
        style={style}
        css={`
          background-color: #eee;
          padding: ${rpx(2)}px ${rpx(5)}px;
          border-radius: ${rpx(3)}px;
        `}
      >
        <Typography.Text light size={rpx(12)} color="#555">
          {label}
        </Typography.Text>
      </Center>
    );
  },
);
